import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { 
  BookOpen, CheckCircle, XCircle, Clock, Calendar,
  TrendingUp, Award, Loader2, AlertTriangle, FileText
} from 'lucide-react';

const TeacherPerformance = () => {
  const [performance, setPerformance] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = authService.getCurrentUser();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [perfResponse, classesResponse] = await Promise.all([
        api.getTeacherPerformance(user.id),
        api.getClasses({ teacher_id: user.id })
      ]);
      setPerformance(perfResponse.data);
      setClasses(classesResponse.data || []);
    } catch (error) {
      toast.error('Failed to fetch performance data');
    } finally {
      setLoading(false);
    }
  };

  // Filter upcoming scheduled classes (future dates)
  const now = new Date();
  const upcomingClasses = classes.filter(cls => 
    cls.status === 'scheduled' && new Date(cls.scheduled_date) > now
  ).sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));

  // Filter classes with pending PDF (completed but no PDF attached)
  const pendingPdfClasses = classes.filter(cls => 
    cls.status === 'completed' && !cls.pdf_attached
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  if (!performance) {
    return (
      <div className="text-center py-12 text-slate-500">
        Failed to load performance data
      </div>
    );
  }

  const stats = performance.stats || {};
  const statCards = [
    {
      title: 'Total Classes',
      value: stats.total_classes || 0,
      icon: BookOpen,
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50'
    },
    {
      title: 'Completed',
      value: stats.completed_classes || 0,
      icon: CheckCircle,
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Cancelled',
      value: stats.cancelled_classes || 0,
      icon: XCircle,
      color: 'bg-red-500',
      bgColor: 'bg-red-50'
    },
    {
      title: 'This Month',
      value: stats.monthly_completed || 0,
      icon: Calendar,
      color: 'bg-amber-500',
      bgColor: 'bg-amber-50'
    }
  ];

  return (
    <div className="space-y-6" data-testid="teacher-performance">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Performance</h1>
          <p className="text-slate-600 mt-1">Your class delivery statistics</p>
        </div>
        <div className="flex items-center gap-3 bg-indigo-50 px-4 py-2 rounded-lg">
          <Award className="text-indigo-600" size={24} />
          <div>
            <p className="text-sm text-slate-600">Completion Rate</p>
            <p className="text-xl font-bold text-indigo-600">{stats.completion_rate || 0}%</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className={`${stat.bgColor} rounded-xl p-4 border border-slate-100`}
            data-testid={`stat-card-${index}`}
          >
            <div className={`${stat.color} w-10 h-10 rounded-lg flex items-center justify-center mb-3`}>
              <stat.icon className="text-white" size={20} />
            </div>
            <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
            <p className="text-sm text-slate-600">{stat.title}</p>
          </div>
        ))}
      </div>

      {/* Monthly Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingUp className="text-indigo-600" size={20} />
          This Month
        </h2>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-slate-600">Total Classes</span>
            <span className="font-semibold text-slate-900">{stats.monthly_classes || 0}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-600">Completed</span>
            <span className="font-semibold text-emerald-600">{stats.monthly_completed || 0}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-600">Pending</span>
            <span className="font-semibold text-amber-600">
              {(stats.monthly_classes || 0) - (stats.monthly_completed || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Pending PDF Alert */}
      {pendingPdfClasses.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">PDF Upload Pending</h3>
              <p className="text-sm text-amber-700 mt-1">
                You have {pendingPdfClasses.length} completed class(es) without PDF attached. Please upload the materials.
              </p>
              <div className="mt-3 space-y-2">
                {pendingPdfClasses.map((cls) => (
                  <div key={cls.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-200">
                    <div>
                      <p className="font-medium text-slate-900">{cls.title}</p>
                      <p className="text-sm text-slate-600">{cls.subject} • {cls.course}</p>
                    </div>
                    <div className="flex items-center gap-2 text-amber-600">
                      <FileText size={16} />
                      <span className="text-sm font-medium">PDF Missing</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Scheduled Classes */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar className="text-indigo-600" size={20} />
          Upcoming Classes
        </h2>
        {upcomingClasses.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Calendar size={48} className="mx-auto mb-4 text-slate-300" />
            <p>No upcoming classes scheduled</p>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingClasses.map((cls) => (
              <div
                key={cls.id}
                className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100"
                data-testid={`upcoming-class-${cls.id}`}
              >
                <div>
                  <h3 className="font-medium text-slate-900">{cls.title}</h3>
                  <p className="text-sm text-slate-600">{cls.subject} • {cls.course}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-slate-900">
                    {new Date(cls.scheduled_date).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-blue-600">
                    {new Date(cls.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Classes */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Clock className="text-indigo-600" size={20} />
          Recent Classes
        </h2>
        {!performance.recent_classes || performance.recent_classes.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Calendar size={48} className="mx-auto mb-4 text-slate-300" />
            <p>No classes recorded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {performance.recent_classes.map((cls) => (
              <div
                key={cls.id}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                data-testid={`recent-class-${cls.id}`}
              >
                <div>
                  <h3 className="font-medium text-slate-900">{cls.title}</h3>
                  <p className="text-sm text-slate-600">{cls.subject} • {cls.course}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-slate-900">
                    {new Date(cls.scheduled_date).toLocaleDateString()}
                  </p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    cls.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                    cls.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {cls.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherPerformance;
